import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Text,
  Spinner,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Collapse,
  Textarea,
} from "@chakra-ui/react";
import { Header, FilterProto, ColumnInfo, ObservationColumnProto } from "build-ts/metadata";
import { BenchmarkType, FilterOperator, AggregationType } from "build-ts/enums";
import { BenchmarkEntityProto, BenchmarkEntityProto_DefinitionMapping } from "build-ts/knowledege_graph";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { DateFilter, DatePeriodEnum } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { ModelInfo } from "build-ts/metadata";
import { epochConverterSecond } from "../../../utils/dateUtils";
import { useEditorShellTokens } from "utils/editorShellStyles";
import { AppIcons } from "constants/icons";
import Observation from "modules/metric/components/Observation";
import FiltersContainer from "modules/metric/conatiners/Filters";

interface FilterWithValues {
  column: Header;
  values: string[];
  operator: FilterOperator;
}

interface MetricDefinitionMapping {
  mapping: BenchmarkEntityProto_DefinitionMapping;
  metricDetails?: Header;
  isLoading: boolean;
}

interface BenchmarkEditFormProps {
  compact?: boolean;
  onClose: () => void;
  benchmark: BenchmarkEntityProto;

  // Loading / saving
  isLoading: boolean;
  isSaving: boolean;

  // Core benchmark state
  benchmarkName: string;
  setBenchmarkName: React.Dispatch<React.SetStateAction<string>>;
  benchmarkDescription: string;
  setBenchmarkDescription: React.Dispatch<React.SetStateAction<string>>;
  benchmarkType: BenchmarkType | undefined;
  selectedGranularity: DatePeriodEnum | undefined;
  setSelectedGranularity: React.Dispatch<React.SetStateAction<DatePeriodEnum | undefined>>;

  // Versus benchmark filter state (KG column-based)
  filters: FilterWithValues[];
  kgColumns: Header[] | undefined;
  selectedKGColumn: Header | null;
  setSelectedKGColumn: React.Dispatch<React.SetStateAction<Header | null>>;
  columnValues: string[];
  selectedValues: string[];
  isValuesLoading: boolean;
  showFilterSelector: boolean;
  setShowFilterSelector: React.Dispatch<React.SetStateAction<boolean>>;
  editFilter: boolean;
  editingFilterIndex: number;
  editingFilterValues: string[];
  handleToggleValue: (value: string) => void;
  handleToggleEditValue: (value: string) => void;
  handleAddFilter: () => void;
  handleRemoveFilter: (index: number) => void;
  handleEditFilter: (index: number) => void;
  handleSaveEditedFilter: () => void;
  handleCancelEditFilter: () => void;
  resetFilterSelector: () => void;

  // Target benchmark definition mapping state
  definitionMappings: MetricDefinitionMapping[];
  showDefinitionModal: boolean;
  setShowDefinitionModal: React.Dispatch<React.SetStateAction<boolean>>;
  availableMetrics: Header[];
  selectedMetricForDefinition: string;
  setSelectedMetricForDefinition: React.Dispatch<React.SetStateAction<string>>;
  targetDataModelHeaders: Header[];
  selectedTargetModelId: string;
  handleTargetModelSelect: (id: string) => void;
  targetModelInfo: ModelInfo | null;
  isLoadingTargetModel: boolean;
  isSavingDefinition: boolean;
  handleSaveDefinitionMapping: () => void;
  handleRemoveDefinitionMapping: (index: number) => void;

  // Observation panel state + handlers (Observation component)
  selectedObservationColumn: Header | null;
  selectedAggregation: AggregationType | undefined;
  selectedObservationColumns: ObservationColumnProto[];
  handleObservationColumnChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAggregationChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAddObservationColumn: () => void;
  handleDeleteObservationColumn: (index: number) => void;

  // Filter panel state (FiltersContainer within definition mapping)
  filterColumnValues: string[];
  setFilterColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  selectedFilterColumn: ColumnInfo | null;
  setSelectedFilterColumn: React.Dispatch<React.SetStateAction<ColumnInfo | null>>;
  selectedOperator: FilterOperator | undefined;
  setSelectedOperator: React.Dispatch<React.SetStateAction<FilterOperator | undefined>>;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  selectedColumnValues: string[];
  setSelectedColumnValues: React.Dispatch<React.SetStateAction<string[]>>;
  selectedFilterColumns: FilterProto[];
  setSelectedFilterColumns: React.Dispatch<React.SetStateAction<FilterProto[]>>;
  dateFilter: DateFilter | undefined;
  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  dateFilterCategory: DateFilterCategory;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
}

const BenchmarkEditForm: React.FC<BenchmarkEditFormProps> = ({
  compact,
  onClose,
  benchmark,
  isLoading,
  isSaving,
  benchmarkName,
  setBenchmarkName,
  benchmarkDescription,
  setBenchmarkDescription,
  benchmarkType,
  selectedGranularity,
  setSelectedGranularity,
  filters,
  kgColumns,
  selectedKGColumn,
  setSelectedKGColumn,
  columnValues,
  selectedValues,
  isValuesLoading,
  showFilterSelector,
  setShowFilterSelector,
  editFilter,
  editingFilterIndex,
  editingFilterValues,
  handleToggleValue,
  handleToggleEditValue,
  handleAddFilter,
  handleRemoveFilter,
  handleEditFilter,
  handleSaveEditedFilter,
  handleCancelEditFilter,
  resetFilterSelector,
  definitionMappings,
  showDefinitionModal,
  setShowDefinitionModal,
  availableMetrics,
  selectedMetricForDefinition,
  setSelectedMetricForDefinition,
  targetDataModelHeaders,
  selectedTargetModelId,
  handleTargetModelSelect,
  targetModelInfo,
  isLoadingTargetModel,
  isSavingDefinition,
  handleSaveDefinitionMapping,
  handleRemoveDefinitionMapping,
  selectedObservationColumn,
  selectedAggregation,
  selectedObservationColumns,
  handleObservationColumnChange,
  handleAggregationChange,
  handleAddObservationColumn,
  handleDeleteObservationColumn,
  filterColumnValues,
  setFilterColumnValues,
  selectedFilterColumn,
  setSelectedFilterColumn,
  selectedOperator,
  setSelectedOperator,
  inputValue,
  setInputValue,
  selectedColumnValues,
  setSelectedColumnValues,
  selectedFilterColumns,
  setSelectedFilterColumns,
  dateFilter,
  setDateFilter,
  dateFilterCategory,
  setDateFilterCategory,
}) => {
  const t = useEditorShellTokens(compact ?? false);

  const getAggregationName = (type?: AggregationType) => {
    switch (type) {
      case AggregationType.SUM:
        return "SUM";
      case AggregationType.AVERAGE:
        return "AVG";
      case AggregationType.MIN:
        return "MIN";
      case AggregationType.MAX:
        return "MAX";
      case AggregationType.COUNT:
        return "COUNT";
      default:
        return "SUM";
    }
  };

  const renderAllFilters = (allFilters: FilterProto[]) => {
    if (!allFilters || allFilters.length === 0) return "";
    const filterTexts = allFilters.map(filter => {
      const columnName = filter.logical_column_header?.name || "Unknown Column";
      if (filter.values && filter.values.length > 0) {
        return `${columnName} = ${filter.values.join(", ")}`;
      } else if (filter.date_filter && filter.date_filter.reference_date_epoc_sec) {
        return `${columnName} = ${epochConverterSecond(filter.date_filter.reference_date_epoc_sec)}`;
      }
      return `${columnName} = (no value)`;
    });
    return ` where ${filterTexts.join(" AND ")}`;
  };

  const SectionBar = ({ label }: { label: string }) => (
    <Box
      bg="gray.50"
      px={t.sectionPx}
      py={t.sectionPy}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      <Text
        fontWeight="600"
        fontSize={t.sectionTitleSize}
        color="gray.600"
        textAlign="left"
        letterSpacing="normal"
        mb={0}
      >
        {label}
      </Text>
    </Box>
  );

  return (
    <Box position="relative">
      {isSaving && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(255,255,255,0.75)"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="md"
        >
          <VStack spacing={2}>
            <Spinner size="md" color="purple.500" thickness="3px" />
            <Text fontSize={t.bodyText} fontWeight="600" color="purple.600" mb={0}>
              Saving…
            </Text>
          </VStack>
        </Box>
      )}

      <VStack spacing={0} align="stretch" opacity={isSaving ? 0.6 : 1}>
        {isLoading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="md" color="purple.500" />
            <Text mt={2} color="gray.500" fontSize={t.bodyText}>
              Loading benchmark…
            </Text>
          </Box>
        ) : benchmark ? (
          <Box>
            {/* Core properties */}
            <SectionBar label="Semantics" />
            <VStack
              spacing={t.vStackSpacing}
              align="stretch"
              px={t.contentPx}
              pt={t.contentPt}
              pb={t.contentPb}
              bg="white"
            >
              <HStack spacing={t.vStackSpacing}>
                <FormControl flex="1">
                  <FormLabel {...t.label}>Name</FormLabel>
                  <Input
                    {...t.field}
                    size={t.inputSize}
                    value={benchmarkName}
                    onChange={e => setBenchmarkName(e.target.value)}
                    placeholder="Benchmark name"
                    isDisabled={isSaving}
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel {...t.label}>Type</FormLabel>
                  <Select {...t.field} size={t.inputSize} value={benchmarkType} isDisabled>
                    <option value={BenchmarkType.VERSUS_BENCHMARK}>Versus Benchmark</option>
                    <option value={BenchmarkType.TARGET_BENCHMARK}>Target Benchmark</option>
                    <option value={BenchmarkType.NO_BENCHMARK}>No Benchmark</option>
                  </Select>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel {...t.label}>Context</FormLabel>
                <Textarea
                  {...t.field}
                  size={t.inputSize}
                  rows={t.textareaRows}
                  value={benchmarkDescription}
                  onChange={e => setBenchmarkDescription(e.target.value)}
                  placeholder="Describe what this benchmark represents and how it should be used…"
                  resize="vertical"
                />
              </FormControl>

              {benchmarkType === BenchmarkType.TARGET_BENCHMARK && (
                <FormControl maxW="240px">
                  <FormLabel {...t.label}>Granularity</FormLabel>
                  <Select
                    {...t.field}
                    size={t.inputSize}
                    value={selectedGranularity}
                    onChange={e => setSelectedGranularity(Number(e.target.value) as DatePeriodEnum)}
                    placeholder="Select granularity"
                    isDisabled={isSaving}
                  >
                    <option value={DatePeriodEnum.DAY}>Day</option>
                    <option value={DatePeriodEnum.WEEK}>Week</option>
                    <option value={DatePeriodEnum.MONTH}>Month</option>
                    <option value={DatePeriodEnum.QUARTER}>Quarter</option>
                    <option value={DatePeriodEnum.YEAR}>Year</option>
                  </Select>
                </FormControl>
              )}
            </VStack>

            {/* Filters / Definitions Section */}
            <SectionBar label={benchmarkType === BenchmarkType.TARGET_BENCHMARK ? "Definition Mappings" : "Filters"} />
            <Box>
              <Box px={t.contentPx} pt={t.contentPt} pb={t.contentPb} bg="white">
                {/* Definition Mappings for TARGET_BENCHMARK */}
                {benchmarkType === BenchmarkType.TARGET_BENCHMARK && (
                  <Box>
                    <Flex justifyContent="space-between" alignItems="center" mb={t.compact ? 2 : 3}>
                      <Text fontWeight="medium" fontSize={t.bodyText}>
                        Definition Mappings
                      </Text>
                      <HStack>
                        <Text fontSize={t.bodyText} color="gray.600">
                          {definitionMappings.length} mapping{definitionMappings.length !== 1 ? "s" : ""}
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<AddIcon />}
                          onClick={() => setShowDefinitionModal(true)}
                          isDisabled={isSaving}
                        >
                          Add Definition
                        </Button>
                      </HStack>
                    </Flex>

                    {/* Inline collapsible add-definition form */}
                    <Collapse in={showDefinitionModal} animateOpacity>
                      <Box borderWidth="1px" borderRadius="md" p={4} mt={4} mb={4} bg="blue.50" borderColor="blue.200">
                        <VStack spacing={4} align="stretch">
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="bold" fontSize={t.panelHeading} color="blue.800">
                              Add Definition Mapping
                            </Text>
                            <Button size="sm" variant="ghost" onClick={() => setShowDefinitionModal(false)}>
                              Cancel
                            </Button>
                          </Flex>

                          {/* Select Metric */}
                          <FormControl isRequired>
                            <FormLabel>Select Metric</FormLabel>
                            <Select
                              placeholder="Select a metric"
                              value={selectedMetricForDefinition}
                              onChange={e => setSelectedMetricForDefinition(e.target.value)}
                              bg="white"
                            >
                              {availableMetrics.map(metric => (
                                <option key={metric.id} value={metric.id}>
                                  {metric.name}
                                </option>
                              ))}
                            </Select>
                            {selectedMetricForDefinition &&
                              definitionMappings.some(m => m.mapping.metric_id === selectedMetricForDefinition) && (
                                <Text color="red.500" fontSize={t.bodyText} mt={2}>
                                  <Icon as={AppIcons.warning} mr={1} />
                                  Warning: This metric already has a definition mapping. Proceeding will override the
                                  existing mapping.
                                </Text>
                              )}
                          </FormControl>

                          {/* Select Target Model */}
                          <FormControl isRequired>
                            <FormLabel>Select Target Model</FormLabel>
                            <Select
                              placeholder="Select a target model"
                              value={selectedTargetModelId}
                              onChange={e => handleTargetModelSelect(e.target.value)}
                              isDisabled={isLoadingTargetModel}
                              bg="white"
                            >
                              {targetDataModelHeaders.map(model => (
                                <option key={model.id} value={model.id}>
                                  {model.name}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          {isLoadingTargetModel && (
                            <Box textAlign="center" py={4}>
                              <Spinner size="sm" />
                              <Text mt={2} fontSize={t.bodyText}>
                                Loading target model...
                              </Text>
                            </Box>
                          )}

                          {/* Observation + Filter panels once target model is selected */}
                          {targetModelInfo && (
                            <Box>
                              <Flex direction="row" gap={4}>
                                {/* Left side — Observations */}
                                <Box flex="1" bg="white" p={3} borderRadius="md" borderWidth="1px">
                                  <Text fontWeight="medium" mb={2} fontSize={t.bodyText}>
                                    Define Observations
                                  </Text>
                                  <Observation
                                    columns={targetModelInfo.columns!}
                                    selectedObservationColumn={selectedObservationColumn}
                                    selectedAggregation={selectedAggregation}
                                    selectedObservationColumns={selectedObservationColumns}
                                    handleObservationColumnChange={handleObservationColumnChange}
                                    handleAggregationChange={handleAggregationChange}
                                    handleAddObservationColumn={handleAddObservationColumn}
                                    handleDeleteObservationColumn={handleDeleteObservationColumn}
                                    compact={compact}
                                  />
                                </Box>

                                {/* Right side — Filters */}
                                <Box flex="1" bg="white" p={3} borderRadius="md" borderWidth="1px">
                                  <Text fontWeight="medium" mb={2} fontSize={t.bodyText}>
                                    Define Filters
                                  </Text>
                                  <FiltersContainer
                                    columns={targetModelInfo.columns!}
                                    columnValues={filterColumnValues}
                                    setColumnValues={setFilterColumnValues}
                                    selectedFilterColumn={selectedFilterColumn}
                                    setSelectedFilterColumn={setSelectedFilterColumn}
                                    selectedOperator={selectedOperator}
                                    setSelectedOperator={setSelectedOperator}
                                    inputValue={inputValue}
                                    setInputValue={setInputValue}
                                    selectedColumnValues={selectedColumnValues}
                                    setSelectedColumnValues={setSelectedColumnValues}
                                    selectedFilterColumns={selectedFilterColumns}
                                    setSelectedFilterColumns={setSelectedFilterColumns}
                                    dateFilter={dateFilter}
                                    setDateFilter={setDateFilter}
                                    dateFilterCategory={dateFilterCategory}
                                    setDateFilterCategory={setDateFilterCategory}
                                    compact={compact}
                                  />
                                </Box>
                              </Flex>
                            </Box>
                          )}

                          {/* Footer buttons */}
                          <Flex justify="flex-end" gap={3} pt={4} borderTop="1px solid" borderColor="blue.200">
                            <Button variant="outline" onClick={() => setShowDefinitionModal(false)}>
                              Cancel
                            </Button>
                            <Button
                              colorScheme="blue"
                              onClick={handleSaveDefinitionMapping}
                              isLoading={isSavingDefinition}
                              isDisabled={
                                !selectedMetricForDefinition ||
                                !selectedTargetModelId ||
                                selectedObservationColumns.length === 0
                              }
                            >
                              Add Definition
                            </Button>
                          </Flex>
                        </VStack>
                      </Box>
                    </Collapse>

                    {definitionMappings.length > 0 ? (
                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Metric</Th>
                              <Th>Definition</Th>
                              <Th width="50px"></Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {definitionMappings.map((mappingItem, index) => (
                              <Tr key={index}>
                                <Td>
                                  {mappingItem?.isLoading ? (
                                    <Spinner size="xs" mr={2} />
                                  ) : (
                                    mappingItem?.metricDetails?.name ||
                                    mappingItem?.mapping?.metric_id ||
                                    "Unknown Metric"
                                  )}
                                </Td>
                                <Td>
                                  {mappingItem?.mapping?.benchmark_definition?.content ? (
                                    <Text fontSize={t.bodyText}>
                                      {(() => {
                                        const col =
                                          mappingItem.mapping.benchmark_definition.content.observation_columns?.[0];
                                        if (!col?.logical_column_header?.name) return "No definition";
                                        const agg = getAggregationName(col.aggregation);
                                        return `${col.logical_column_header.name} (${agg})`;
                                      })()}
                                      {renderAllFilters(mappingItem.mapping.benchmark_definition.content.filters)}
                                    </Text>
                                  ) : (
                                    <Text fontSize={t.bodyText} color="gray.500">
                                      No definition
                                    </Text>
                                  )}
                                </Td>
                                <Td>
                                  <IconButton
                                    aria-label="Remove mapping"
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleRemoveDefinitionMapping(index)}
                                    isDisabled={isSaving}
                                  />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                        <Text color="gray.500" fontSize={t.bodyText}>
                          No definition mappings found. This benchmark is not connected to any metrics.
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Filters for VERSUS_BENCHMARK */}
                {benchmarkType === BenchmarkType.VERSUS_BENCHMARK && (
                  <Box>
                    <Flex justify="space-between" align="center" mb={t.compact ? 2 : 3}>
                      <Text fontWeight="medium" fontSize={t.bodyText}>
                        Filters
                      </Text>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<AddIcon />}
                        onClick={() => setShowFilterSelector(true)}
                        isDisabled={isSaving}
                      >
                        Add Filter
                      </Button>
                    </Flex>

                    {/* Existing filters list */}
                    {filters.length > 0 ? (
                      <TableContainer mb={4}>
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Column</Th>
                              <Th>Values</Th>
                              <Th width="50px"></Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {filters.map((filter, index) => (
                              <Tr key={index}>
                                <Td>{filter.column.name}</Td>
                                <Td>
                                  <Text fontSize={t.bodyText} noOfLines={2} m={0}>
                                    {filter.values.join(", ")}
                                  </Text>
                                </Td>
                                <Td>
                                  <HStack>
                                    <IconButton
                                      aria-label="Edit filter"
                                      icon={<EditIcon />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="green"
                                      onClick={() => handleEditFilter(index)}
                                      isDisabled={isSaving}
                                    />
                                    <IconButton
                                      aria-label="Remove filter"
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="red"
                                      onClick={() => handleRemoveFilter(index)}
                                      isDisabled={isSaving}
                                    />
                                  </HStack>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50" mb={4}>
                        <Text color="gray.500" fontSize={t.bodyText}>
                          No filters added. Add a filter to define your benchmark.
                        </Text>
                      </Box>
                    )}

                    {/* Add-filter panel */}
                    {showFilterSelector && (
                      <Box borderWidth="1px" borderRadius="md" p={4} mb={4} bg="gray.50">
                        <VStack spacing={4} align="stretch">
                          <FormControl>
                            <FormLabel>KG Column</FormLabel>
                            <Select
                              value={selectedKGColumn?.id || ""}
                              onChange={e => {
                                const selected = kgColumns?.find(col => col.id === e.target.value) || null;
                                setSelectedKGColumn(selected);
                              }}
                              placeholder="Select KG column"
                              isDisabled={isSaving}
                            >
                              {kgColumns?.map(column => {
                                const isAlreadyUsed = filters.some(f => f.column.id === column.id);
                                const shouldDisable = filters.length > 0 && !isAlreadyUsed;
                                return (
                                  <option key={column.id} value={column.id} disabled={shouldDisable}>
                                    {column.name}
                                  </option>
                                );
                              })}
                            </Select>
                          </FormControl>

                          {selectedKGColumn && (
                            <FormControl>
                              <FormLabel>Select Values</FormLabel>
                              {isValuesLoading ? (
                                <Box textAlign="center" py={4}>
                                  <Spinner size="sm" color="blue.500" />
                                  <Text mt={2} fontSize={t.bodyText}>
                                    Loading values...
                                  </Text>
                                </Box>
                              ) : columnValues.length > 0 ? (
                                <Box
                                  maxH="200px"
                                  overflowY="auto"
                                  border="1px"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  p={2}
                                  bg="white"
                                >
                                  {columnValues.map((value, index) => (
                                    <Box key={index} py={1}>
                                      <Checkbox
                                        isChecked={selectedValues.includes(value)}
                                        onChange={() => handleToggleValue(value)}
                                        colorScheme="blue"
                                        isDisabled={isSaving}
                                      >
                                        {value}
                                      </Checkbox>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Text color="gray.500">No values available for this column</Text>
                              )}
                            </FormControl>
                          )}

                          <HStack spacing={3} justify="flex-end">
                            <Button size="sm" variant="outline" onClick={resetFilterSelector} isDisabled={isSaving}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              onClick={handleAddFilter}
                              isDisabled={!selectedKGColumn || selectedValues.length === 0 || isSaving}
                            >
                              Add
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    )}

                    {/* Edit-filter panel */}
                    {editFilter && editingFilterIndex >= 0 && (
                      <Box borderWidth="1px" borderRadius="md" p={4} mb={4} bg="yellow.50" borderColor="yellow.200">
                        <VStack spacing={4} align="stretch">
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="bold" fontSize={t.panelHeading} color="yellow.800">
                              Edit Filter: {filters[editingFilterIndex]?.column?.name}
                            </Text>
                            <Button size="sm" variant="ghost" onClick={handleCancelEditFilter}>
                              Cancel
                            </Button>
                          </Flex>

                          <FormControl>
                            <FormLabel>Select Values</FormLabel>
                            {isValuesLoading ? (
                              <Box textAlign="center" py={4}>
                                <Spinner size="sm" color="blue.500" />
                                <Text mt={2} fontSize={t.bodyText}>
                                  Loading values...
                                </Text>
                              </Box>
                            ) : columnValues.length > 0 ? (
                              <Box
                                maxH="200px"
                                overflowY="auto"
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={2}
                                bg="white"
                              >
                                {columnValues.map((value, index) => (
                                  <Box key={index} py={1}>
                                    <Checkbox
                                      isChecked={editingFilterValues.includes(value)}
                                      onChange={() => handleToggleEditValue(value)}
                                      colorScheme="blue"
                                      isDisabled={isSaving}
                                    >
                                      {value}
                                    </Checkbox>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Text color="gray.500">No values available for this column</Text>
                            )}
                          </FormControl>

                          <HStack spacing={3} justify="flex-end">
                            <Button size="sm" variant="outline" onClick={handleCancelEditFilter} isDisabled={isSaving}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              onClick={handleSaveEditedFilter}
                              isDisabled={editingFilterValues.length === 0 || isSaving}
                            >
                              Save Changes
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box textAlign="center" py={10}>
            <Text fontSize={t.bodyText} color="gray.400">
              No benchmark data available.
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default BenchmarkEditForm;
